'use client'

import React from 'react'

type Props = {
    pressureAtm: number
    volumeLiters: number
    moles: number
    temperatureK: number
}

const WIDTH = 340
const HEIGHT = 200
const PAD_LEFT = 40
const PAD_RIGHT = 12
const PAD_TOP = 14
const PAD_BOTTOM = 28
const PLOT_W = WIDTH - PAD_LEFT - PAD_RIGHT
const PLOT_H = HEIGHT - PAD_TOP - PAD_BOTTOM
const R = 0.082057

export default function IdealGasLawChart({ pressureAtm, volumeLiters, moles, temperatureK }: Props) {
    const n = Math.max(moles, 0.0001)
    const t = Math.max(temperatureK, 0.0001)
    const v0 = Math.max(volumeLiters, 0.0001)

    const vMin = v0 * 0.25
    const vMax = v0 * 3
    const pAtVMin = (n * R * t) / vMin
    const pAtVMax = (n * R * t) / vMax
    const pMax = Math.max(pAtVMin, pressureAtm) * 1.1
    const pMin = 0

    const xToSvg = (v: number) => PAD_LEFT + ((v - vMin) / (vMax - vMin)) * PLOT_W
    const yToSvg = (p: number) => PAD_TOP + PLOT_H - ((p - pMin) / (pMax - pMin)) * PLOT_H

    const points: string[] = []
    const steps = 60
    for (let i = 0; i <= steps; i++) {
        const v = vMin + ((vMax - vMin) * i) / steps
        const p = (n * R * t) / v
        points.push(`${xToSvg(v).toFixed(1)},${yToSvg(Math.min(p, pMax)).toFixed(1)}`)
    }

    const markerX = xToSvg(v0)
    const markerY = yToSvg(Math.min(pressureAtm, pMax))

    return (
        <svg width={WIDTH} height={HEIGHT} viewBox={`0 0 ${WIDTH} ${HEIGHT}`} style={{ maxWidth: '100%', height: 'auto' }}>
            {/* axes */}
            <line x1={PAD_LEFT} y1={PAD_TOP} x2={PAD_LEFT} y2={HEIGHT - PAD_BOTTOM} stroke="#9ca3af" strokeWidth={1} />
            <line x1={PAD_LEFT} y1={HEIGHT - PAD_BOTTOM} x2={WIDTH - PAD_RIGHT} y2={HEIGHT - PAD_BOTTOM} stroke="#9ca3af" strokeWidth={1} />
            <text x={PAD_LEFT} y={HEIGHT - 8} fontSize={10} fill="#6b7280">V (L)</text>
            <text x={4} y={PAD_TOP + 6} fontSize={10} fill="#6b7280">P (atm)</text>
            {/* isotherm curve */}
            <polyline points={points.join(' ')} fill="none" stroke="#2563eb" strokeWidth={2} />
            {/* current point marker */}
            <line x1={markerX} y1={markerY} x2={markerX} y2={HEIGHT - PAD_BOTTOM} stroke="#dc2626" strokeWidth={1} strokeDasharray="3,3" opacity={0.6} />
            <circle cx={markerX} cy={markerY} r={4.5} fill="#dc2626" stroke="#fff" strokeWidth={1.5} />
        </svg>
    )
}
